/**
 * §7.22 Feed Health · Sovereignty tab.
 *
 * Renders the structured coverage-tier register so operators can see at a
 * glance: (a) which countries do NOT publish official surveillance data
 * (non-disseminators — DPRK / Eritrea / Turkmenistan / etc.); (b) which
 * wired adapters ship with a structural blockedReason and what the unblock
 * path is; (c) which adapters are low-yield-by-design so quiet weeks aren't
 * mistaken for silent failures.
 *
 * The data is loaded from `data/coverage-tier.json` in the surveillance-feed
 * plugin via `loadCoverageTier()` — keep the JSON in sync with
 * `docs/country-coverage-census.md` (the markdown is the human-narrative;
 * this surface is the operator-facing index).
 */

import {
  loadCoverageTier,
  type NonDisseminator,
  type BlockedAdapterEntry,
  type LowYieldByDesignEntry,
  type StagedAdapterEntry,
} from '@biomax/plugin-surveillance-feed';

export function SovereigntyView() {
  const reg = loadCoverageTier();
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <SectionIntro lastReviewed={reg.lastReviewed} />
      <NonDisseminatorTable rows={reg.nonDisseminators} />
      <StagedAdapterTable rows={reg.stagedAdapters ?? []} />
      <BlockedAdapterTable rows={reg.blockedAdapters} />
      <LowYieldByDesignTable rows={reg.lowYieldByDesign} />
    </div>
  );
}

function StagedAdapterTable({ rows }: { rows: readonly StagedAdapterEntry[] }) {
  if (rows.length === 0) return null;
  return (
    <section>
      <h3 style={sectionHeader}>
        Staged — wire-ready, live fetch deferred
        <span style={countPill}>{rows.length}</span>
      </h3>
      <p style={sectionLede}>
        Adapters built + fixture-tested but whose LIVE fetch is deferred pending
        a confirmed URL pattern or partner channel. <b>Staged ≠ dark, and ≠ down:</b>{' '}
        each jurisdiction is covered at event-level by the live multilateral
        sources named below until its national feed is confirmed. The liveness
        harness counts these as "staged", never as a failure.
      </p>
      <div style={tableWrap}>
        <table style={table}>
          <thead><tr>
            <th style={th}>Adapter id</th>
            <th style={th}>Why deferred</th>
            <th style={th}>Interim live coverage</th>
          </tr></thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.id}>
                <td style={tdMono}>{r.id}</td>
                <td style={tdProse}>{r.reason}</td>
                <td style={tdProse}>
                  <span style={{ color: 'var(--bm-prov-confirmed)' }}>✓ {r.interimCoverage}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function SectionIntro({ lastReviewed }: { lastReviewed: string }) {
  return (
    <div style={{
      padding: 12,
      background: 'var(--bm-bg-subtle)',
      border: '1px solid var(--bm-border)',
      borderRadius: 'var(--bm-radius-sm)',
      fontSize: 12.5, lineHeight: 1.55, color: 'var(--bm-text-muted)',
    }}>
      The honest-empty rail. Three different reasons a country / signal looks
      quiet, with different operator actions for each. Don't conflate
      structural blocks (non-disseminators) with transient failures
      (DEGRADED) or with intentionally event-driven sources (low-yield).
      <span style={{ color: 'var(--bm-text-subtle)', marginLeft: 6 }}>
        Register last reviewed {lastReviewed}.
      </span>
    </div>
  );
}

function NonDisseminatorTable({ rows }: { rows: readonly NonDisseminator[] }) {
  return (
    <section>
      <h3 style={sectionHeader}>
        Non-disseminator countries
        <span style={countPill}>{rows.length}</span>
      </h3>
      <p style={sectionLede}>
        Structural information control — no adapter possible without regime
        or policy change. Operators briefing on these regions MUST flag the
        dark spot rather than treat absence-of-signal as evidence-of-absence.
      </p>
      <div style={tableWrap}>
        <table style={table}>
          <thead><tr>
            <th style={th}>Country</th>
            <th style={th}>WHO region</th>
            <th style={th}>Why withheld</th>
            <th style={th}>Indirect inference</th>
          </tr></thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.iso3}>
                <td style={td}>
                  <strong>{r.name}</strong>
                  <div style={mono}>{r.iso3}</div>
                </td>
                <td style={td}>{r.region}</td>
                <td style={tdProse}>{r.rationale}</td>
                <td style={tdProse}>
                  <ul style={ul}>
                    {r.indirectInferenceSources.map((s, i) => <li key={i}>{s}</li>)}
                  </ul>
                  {r.operatorAction && (
                    <div style={operatorAction}>
                      <strong>Operator action:</strong> {r.operatorAction}
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function BlockedAdapterTable({ rows }: { rows: readonly BlockedAdapterEntry[] }) {
  return (
    <section>
      <h3 style={sectionHeader}>
        Wire-ready adapters with structural blockedReason
        <span style={countPill}>{rows.length}</span>
      </h3>
      <p style={sectionLede}>
        Different from non-disseminators: an adapter EXISTS for each of these
        but ships flagged BLOCKED (Cloudflare anti-bot · OFAC sanctions ·
        partnership-required · geo-fence). Operator action is compliance /
        allowlist / MoU, NOT "retry / endpoint check".
      </p>
      <div style={tableWrap}>
        <table style={table}>
          <thead><tr>
            <th style={th}>Adapter id</th>
            <th style={th}>Reason</th>
            <th style={th}>Unblock path</th>
            <th style={th}>Operator action</th>
          </tr></thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.id}>
                <td style={tdMono}>
                  {r.id}
                  {r.parallelFallback && (
                    <div style={partialBadge} title={r.parallelFallback.rationale}>
                      ◐ PARTIAL · {r.parallelFallback.scope}
                    </div>
                  )}
                </td>
                <td style={td}>
                  <span style={reasonPill(r.blockedReason)}>{r.blockedReason}</span>
                </td>
                <td style={tdProse}>
                  {r.unblockPath}
                  {r.parallelFallback && (
                    <div style={fallbackNote}>
                      <strong style={{ color: 'var(--bm-prov-confirmed)' }}>✓ {r.parallelFallback.scope} live</strong>
                      {' '}via <code style={mono}>{r.parallelFallback.id}</code> ({r.parallelFallback.license}).
                      Remaining streams stay blocked until the unblock path lands.
                    </div>
                  )}
                </td>
                <td style={tdProse}>{r.operatorAction}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p style={{ ...sectionLede, marginTop: 10, marginBottom: 0 }}>
        <span style={{ color: 'var(--bm-prov-synthetic)' }}>◐ PARTIAL</span>{' '}
        means a parallel open-data fallback on different infrastructure covers
        SOME streams (e.g. QLD: COVID live via data.qld.gov.au CKAN; flu + RSV
        still Cloudflare-blocked). One LIVE or one BLOCKED badge would erase
        half the truth — the per-jurisdiction reality is mixed.
      </p>
    </section>
  );
}

function LowYieldByDesignTable({ rows }: { rows: readonly LowYieldByDesignEntry[] }) {
  if (rows.length === 0) return null;
  return (
    <section>
      <h3 style={sectionHeader}>
        Low-yield by source design
        <span style={countPill}>{rows.length}</span>
      </h3>
      <p style={sectionLede}>
        Adapters whose source format yields zero rows during quiet periods.
        Suppress "0 signals → silent failure" alerts on these; the absence
        is meaningful (no outbreaks reported), not pathological.
      </p>
      <div style={tableWrap}>
        <table style={table}>
          <thead><tr>
            <th style={th}>Adapter id</th>
            <th style={th}>Why low-yield</th>
            <th style={th}>Operator action</th>
          </tr></thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.id}>
                <td style={tdMono}>{r.id}</td>
                <td style={tdProse}>{r.rationale}</td>
                <td style={tdProse}>{r.operatorAction}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

// ---- Inline styles (matches the rest of FeedHealth's pattern) -------------

const sectionHeader = {
  display: 'flex', alignItems: 'center', gap: 8,
  margin: '0 0 6px 0',
  fontSize: 13.5, fontWeight: 700, color: 'var(--bm-text)',
} as const;

const countPill = {
  display: 'inline-grid', placeItems: 'center',
  minWidth: 22, height: 18, padding: '0 6px',
  borderRadius: 9,
  fontFamily: 'var(--bm-font-mono)', fontSize: 10.5, fontWeight: 700,
  background: 'var(--bm-bg-subtle)',
  border: '1px solid var(--bm-border)',
  color: 'var(--bm-text-muted)',
} as const;

const sectionLede = {
  margin: '0 0 10px 0',
  fontSize: 12, lineHeight: 1.55,
  color: 'var(--bm-text-muted)',
} as const;

const tableWrap = { overflowX: 'auto' as const };

const table = {
  width: '100%', borderCollapse: 'collapse' as const,
  fontSize: 11.5, lineHeight: 1.5,
};

const th = {
  textAlign: 'left' as const,
  padding: '6px 10px',
  fontFamily: 'var(--bm-font-mono)', fontSize: 10, fontWeight: 700,
  letterSpacing: '.4px', textTransform: 'uppercase' as const,
  color: 'var(--bm-text-subtle)',
  borderBottom: '1px solid var(--bm-border)',
  verticalAlign: 'bottom' as const,
};

const td = {
  padding: '8px 10px',
  verticalAlign: 'top' as const,
  borderBottom: '1px solid var(--bm-border)',
};

const tdProse = { ...td, maxWidth: 360 };
const tdMono = { ...td, fontFamily: 'var(--bm-font-mono)', fontSize: 11 };
const mono = { fontFamily: 'var(--bm-font-mono)', fontSize: 10, color: 'var(--bm-text-subtle)', marginTop: 2 };

// CD R11 #6 — partial-coverage badge + fallback note.
const partialBadge = {
  display: 'inline-block', marginTop: 4, padding: '1px 6px',
  borderRadius: 4,
  fontFamily: 'var(--bm-font-mono)', fontSize: 9, fontWeight: 700,
  letterSpacing: '.4px', textTransform: 'uppercase' as const,
  color: 'var(--bm-prov-synthetic)',
  background: 'var(--bm-prov-synthetic-bg)',
  border: '1px solid var(--bm-prov-synthetic)',
} as const;

const fallbackNote = {
  marginTop: 6, padding: '4px 8px',
  fontSize: 11, lineHeight: 1.5,
  color: 'var(--bm-text-muted)',
  background: 'var(--bm-prov-confirmed-bg)',
  border: '1px solid var(--bm-prov-confirmed)',
  borderRadius: 4,
} as const;

const ul = { margin: '0 0 0 16px', padding: 0, color: 'var(--bm-text-muted)' };

const operatorAction = {
  marginTop: 6, padding: '4px 8px',
  background: 'var(--bm-accent-subtle, rgba(110,163,255,0.08))',
  border: '1px solid var(--bm-accent, #6ea3ff)',
  borderRadius: 4,
  fontSize: 11, color: 'var(--bm-text)',
} as const;

function reasonPill(_reason: string) {
  return {
    display: 'inline-block', padding: '2px 7px',
    borderRadius: 4,
    fontFamily: 'var(--bm-font-mono)', fontSize: 10, fontWeight: 700,
    textTransform: 'uppercase' as const, letterSpacing: '.4px',
    color: 'var(--bm-state-blocked, #c66)',
    background: 'var(--bm-prov-blocked-bg, rgba(204,102,102,0.12))',
    border: '1px solid var(--bm-state-blocked, #c66)',
  } as const;
}
